import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { catchError, tap } from 'rxjs/operators';
import { MemberStatusQueryService } from './member-status-query.service';
import {
  LoadCurrentMemberStatus,
  LoadCurrentMemberStatusFailure,
  LoadCurrentMemberStatusSuccess,
  LoadMemberStatusAtEffectiveDate,
  LoadMemberStatusAtEffectiveDateFailure,
  LoadMemberStatusAtEffectiveDateSuccess,
  SelectCurrentMemberAndDate,
  SelectCurrentMemberAndEffectiveDate
} from './member-status.actions';
import { GlobalMemberStatus } from './member-status.model';

export class MemberStatusStateModel {
  currentMemberStatus: GlobalMemberStatus;
  memberStatusAtEffectiveDate: GlobalMemberStatus;
  currentDate: Date;
  effectiveDate: Date;
  loading = false;
  memberId: string;
}

@State<MemberStatusStateModel>({
  name: 'memberStatuses',
  defaults: new MemberStatusStateModel()
})
@Injectable()
export class MemberStatusState {
  constructor(
    private queryService: MemberStatusQueryService,
  ) {}

  @Selector()
  static currentMemberStatus(state: MemberStatusStateModel): GlobalMemberStatus {
    return state.currentMemberStatus;
  }

  @Selector()
  static memberStatusAtEffectiveDate(state: MemberStatusStateModel): GlobalMemberStatus {
    return state.memberStatusAtEffectiveDate;
  }

  @Selector()
  static loading(state: MemberStatusStateModel): boolean {
    return state.loading;
  }

  @Action(SelectCurrentMemberAndDate)
  onSelectCurrentMemberAndDate(
    { patchState, dispatch }: StateContext<MemberStatusStateModel>,
    { memberId, currentDate }: SelectCurrentMemberAndDate
  ) {
    patchState({ memberId, currentDate });
    dispatch(new LoadCurrentMemberStatus());
  }

  @Action(LoadCurrentMemberStatus)
  onLoadCurrentMemberStatus({
    patchState,
    dispatch,
    getState
  }: StateContext<MemberStatusStateModel>) {
    patchState({ loading: true });
    return this.queryService
      .getMemberStatus(getState().memberId, getState().currentDate)
      .pipe(
        tap((res) => dispatch(new LoadCurrentMemberStatusSuccess(res))),
        catchError((error) => dispatch(new LoadCurrentMemberStatusFailure(error)))
      );
  }

  @Action(LoadCurrentMemberStatusSuccess)
  onLoadCurrentMemberStatusSuccess(
    { patchState }: StateContext<MemberStatusStateModel>,
    { data }: LoadCurrentMemberStatusSuccess
  ) {
    patchState({ currentMemberStatus: data, loading: false });
  }

  @Action(LoadCurrentMemberStatusFailure)
  onLoadCurrentMemberStatusFailure(
    { patchState }: StateContext<MemberStatusStateModel>,
    _: LoadCurrentMemberStatusFailure
  ) {
    patchState({ currentMemberStatus: null, loading: false });
  }

  @Action(SelectCurrentMemberAndEffectiveDate)
  onSelectCurrentMemberAndEffectiveDate(
    { patchState, dispatch }: StateContext<MemberStatusStateModel>,
    { memberId, effectiveDate }: SelectCurrentMemberAndEffectiveDate
  ) {
    patchState({ memberId, effectiveDate });
    dispatch(new LoadMemberStatusAtEffectiveDate());
  }

  @Action(LoadMemberStatusAtEffectiveDate)
  onLoadMemberStatusAtEffectiveDate({
    patchState,
    dispatch,
    getState
  }: StateContext<MemberStatusStateModel>) {
    patchState({ loading: true });
    return this.queryService
      .getMemberStatus(getState().memberId, getState().effectiveDate)
      .pipe(
        tap((res) => dispatch(new LoadMemberStatusAtEffectiveDateSuccess(res))),
        catchError((error) => dispatch(new LoadMemberStatusAtEffectiveDateFailure(error)))
      );
  }

  @Action(LoadMemberStatusAtEffectiveDateSuccess)
  onLoadMemberStatusAtEffectiveDateSuccess(
    { patchState }: StateContext<MemberStatusStateModel>,
    { data }: LoadMemberStatusAtEffectiveDateSuccess
  ) {
    patchState({ memberStatusAtEffectiveDate: data, loading: false });
  }

  @Action(LoadMemberStatusAtEffectiveDateFailure)
  onLoadMemberStatusAtEffectiveDateFailure(
    { patchState }: StateContext<MemberStatusStateModel>,
    _: LoadMemberStatusAtEffectiveDateFailure
  ) {
    patchState({ memberStatusAtEffectiveDate: null, loading: false });
  }
}
